// EmailSyncSettings.js - Email Integration Settings Page

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, CircularProgress, Alert,
  Stack, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, Radio, FormControlLabel, FormControl, FormLabel, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Add as AddIcon,
  LinkOff as DisconnectIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  Google as GoogleIcon,
  Settings as SettingsIcon,
  Inbox as InboxIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { emailSyncService } from './activityCommService';
import {
  EmailSyncProviderLabels, EmailSyncProviderOptions,
  formatDateTime
} from './activityCommEnums';

const EmailSyncSettings = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('GMAIL');
  const [setting, setSetting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);
  const [bccAddress, setBccAddress] = useState(null);

  useEffect(() => {
    document.title = 'Email Sync | Activity & Communications';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadConfigs();
    }
  }, [canView]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) setLoading(false);
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const res = await emailSyncService.getConfigs();
      setConfigs(res.data || res || []);
    } catch (err) {
      enqueueSnackbar('Failed to load email sync configurations', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setSetting(true);
    setBccAddress(null);
    try {
      const res = await emailSyncService.setup({ provider: selectedProvider });
      const data = res.data || res;

      if (selectedProvider === 'BCC_DROPBOX' && data.bcc_address) {
        setBccAddress(data.bcc_address);
        enqueueSnackbar('BCC Dropbox configured successfully', { variant: 'success' });
      } else if (data.oauth_url) {
        window.open(data.oauth_url, '_blank');
        enqueueSnackbar('Opening authentication window...', { variant: 'info' });
        setSetupOpen(false);
      } else {
        enqueueSnackbar('Email sync configured successfully', { variant: 'success' });
        setSetupOpen(false);
      }
      loadConfigs();
    } catch (err) {
      enqueueSnackbar('Failed to setup email sync', { variant: 'error' });
      console.error(err);
    } finally {
      setSetting(false);
    }
  };

  const handleDisconnect = async (configId) => {
    try {
      await emailSyncService.disconnect(configId);
      enqueueSnackbar('Email sync disconnected', { variant: 'success' });
      setConfirmDisconnect(null);
      loadConfigs();
    } catch (err) {
      enqueueSnackbar('Failed to disconnect email sync', { variant: 'error' });
      console.error(err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    }).catch(() => {
      enqueueSnackbar('Failed to copy', { variant: 'error' });
    });
  };

  const getProviderIcon = (provider) => {
    if (provider === 'GMAIL') return <GoogleIcon sx={{ color: '#ea4335' }} />;
    if (provider === 'OUTLOOK') return <SettingsIcon sx={{ color: '#0078d4' }} />;
    return <InboxIcon sx={{ color: '#666' }} />;
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'active' || statusLower === 'connected') return 'success';
    if (statusLower === 'error') return 'error';
    if (statusLower === 'paused') return 'warning';
    return 'default';
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view this page.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/activity/dashboard`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Email Sync
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Connect and sync your email accounts
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSetupOpen(true); setBccAddress(null); }}>
            Setup Email Sync
          </Button>
        )}
      </Box>

      {/* Info Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Alert severity="info" icon={<GoogleIcon />}>
            <strong>Gmail</strong> - Connect your Gmail account via OAuth for automatic two-way email sync.
          </Alert>
        </Grid>
        <Grid item xs={12} md={4}>
          <Alert severity="info" icon={<SettingsIcon />}>
            <strong>Outlook</strong> - Connect your Microsoft Outlook account for automatic email sync with your CRM.
          </Alert>
        </Grid>
        <Grid item xs={12} md={4}>
          <Alert severity="info" icon={<InboxIcon />}>
            <strong>BCC Dropbox</strong> - Get a unique email address to BCC on outgoing emails. They are automatically logged in the CRM.
          </Alert>
        </Grid>
      </Grid>

      {/* Configs List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (Array.isArray(configs) && configs.length > 0) ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>BCC Address</TableCell>
                <TableCell>Last Synced</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getProviderIcon(config.provider)}
                      <Typography variant="body2" fontWeight={600}>
                        {EmailSyncProviderLabels[config.provider] || config.provider}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.status || 'Active'}
                      color={getStatusColor(config.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {config.bcc_address ? (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {config.bcc_address}
                        </Typography>
                        <Tooltip title="Copy BCC address">
                          <IconButton size="small" onClick={() => copyToClipboard(config.bcc_address)}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {config.last_synced ? formatDateTime(config.last_synced) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {canModify && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DisconnectIcon />}
                        onClick={() => setConfirmDisconnect(config.id)}
                      >
                        Disconnect
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No email sync configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Connect your email to automatically track communications in the CRM.
          </Typography>
          {canModify && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSetupOpen(true); setBccAddress(null); }}>
              Setup Email Sync
            </Button>
          )}
        </Paper>
      )}

      {/* Setup Dialog */}
      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Setup Email Sync</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
            <FormLabel component="legend">Select Provider</FormLabel>
            <RadioGroup
              value={selectedProvider}
              onChange={(e) => { setSelectedProvider(e.target.value); setBccAddress(null); }}
            >
              {EmailSyncProviderOptions.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {getProviderIcon(opt.value)}
                      <Typography>{opt.label}</Typography>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {selectedProvider === 'BCC_DROPBOX' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>How BCC Dropbox Works:</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li>A unique email address is generated for your account.</li>
                <li>Add this address as BCC on any outgoing email you want tracked.</li>
                <li>Emails sent with this BCC address are automatically logged in the CRM.</li>
                <li>No OAuth or password required.</li>
              </ul>
            </Alert>
          )}

          {bccAddress && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" gutterBottom>Your BCC Dropbox Address:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={bccAddress}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => copyToClipboard(bccAddress)}
                >
                  Copy
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Add this address as BCC when sending emails to automatically log them.
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupOpen(false)}>
            {bccAddress ? 'Done' : 'Cancel'}
          </Button>
          {!bccAddress && (
            <Button variant="contained" onClick={handleSetup} disabled={setting}>
              {setting ? 'Setting up...' : 'Setup'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm Disconnect Dialog */}
      <Dialog open={!!confirmDisconnect} onClose={() => setConfirmDisconnect(null)}>
        <DialogTitle>Disconnect Email Sync</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect this email sync? Previously synced emails will
            remain in the CRM, but no new emails will be imported.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisconnect(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDisconnect(confirmDisconnect)}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailSyncSettings;
